import React, { useState, useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';
import SimpleCard from '../../components/templates/SimpleCard';
import Button from '../../components/Button';
import Modal, { ModalHead, ModalWrapper, ModalBody } from '../../components/Modal';
import apiRequestService from '../../axios/services/apiRequestService';
import filesService from '../../axios/services/filesService';
import styles from './requestApi.module.scss';

const RequestAPI = () => {
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTechDocsModal, setShowTechDocsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState({});
  const { addToast } = useToasts();
  const [formData, setFormData] = useState({
    applicationName: '',
    websiteUrl: '',
    platform: [],
    serverIpAddress: ''
  });

  const platformOptions = [
    { value: 'website', label: 'Website' },
    { value: 'android', label: 'Android' },
    { value: 'ios', label: 'iOS' },
    { value: 'backend', label: 'Backend System / Server' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlatformChange = (platformValue) => {
    setFormData(prev => ({
      ...prev,
      platform: prev.platform.includes(platformValue)
        ? prev.platform.filter(p => p !== platformValue)
        : [...prev.platform, platformValue]
    }));
  };

  const validateForm = () => {
    if (!formData.applicationName.trim()) {
      addToast('Nama aplikasi/website harus diisi', { appearance: 'error' });
      return false;
    }
    if (!formData.websiteUrl.trim()) {
      addToast('URL website harus diisi', { appearance: 'error' });
      return false;
    }
    if (formData.platform.length === 0) {
      addToast('Pilih minimal satu platform', { appearance: 'error' });
      return false;
    }
    if (!formData.serverIpAddress.trim()) {
      addToast('IP Address server harus diisi', { appearance: 'error' });
      return false;
    }
    
    // Validate URL format
    try {
      new URL(formData.websiteUrl);
    } catch {
      addToast('Format URL tidak valid', { appearance: 'error' });
      return false;
    }

    // Validate IP address format
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipList = formData.serverIpAddress.split(',').map(ip => ip.trim());
    for (let ip of ipList) {
      if (!ipRegex.test(ip)) {
        addToast(`IP Address tidak valid: ${ip}`, { appearance: 'error' });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await apiRequestService.submitApiRequest(formData);
      
      addToast('API Access berhasil dibuat dengan client credentials', { appearance: 'success' });
      setShowModal(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      addToast('Gagal membuat API access', { appearance: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await apiRequestService.getApiRequests();
      console.log('API Response:', response); // Debug log
      
      // Handle nested data structure: response.data.data.data
      let data = [];
      if (response.data?.data?.data) {
        data = response.data.data.data;
      } else if (response.data?.data) {
        data = response.data.data;
      } else if (response.data) {
        data = response.data;
      }
      
      console.log('Extracted data:', data); // Debug log
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching API requests:', error);
      addToast('Gagal mengambil data request API', { appearance: 'error' });
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard with visual feedback
  const copyToClipboard = async (text, type = 'API Key') => {
    try {
      await navigator.clipboard.writeText(text);
      addToast(`${type} berhasil disalin!`, {
        appearance: 'success'
      });
    } catch (err) {
      addToast('Gagal menyalin ke clipboard', {
        appearance: 'error'
      });
    }
  };

  // Revoke API request
  const revokeApiRequest = async (requestId) => {
    if (!window.confirm('Apakah Anda yakin ingin mencabut request API ini? Tindakan ini tidak dapat dibatalkan.')) {
      return;
    }

    try {
      setRevokeLoading(prev => ({ ...prev, [requestId]: true }));
      await apiRequestService.revokeApiRequest(requestId);
      addToast('Request API berhasil dicabut', { appearance: 'success' });
      fetchRequests();
    } catch (error) {
      console.error('Error revoking API request:', error);
      const errorMessage = error.response?.data?.message || 'Gagal mencabut request API';
      addToast(errorMessage, { appearance: 'error' });
    } finally {
      setRevokeLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      applicationName: '',
      websiteUrl: '',
      platform: [],
      serverIpAddress: ''
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleDownloadTechDocs = async () => {
    try {
      // Open PDF directly in new tab for better reliability
      const apiBaseUrl = process.env.REACT_APP_BASE_API || 'http://localhost:3000';
      window.open(`${apiBaseUrl}/files/tech-docs/download`, '_blank');
      
      addToast('Tech docs berhasil dibuka!', { appearance: 'success' });
    } catch (error) {
      console.error('Error opening tech docs:', error);
      addToast('Gagal membuka tech docs', { appearance: 'error' });
    }
  };

  const getStatusBadge = (status, rejectionReason = null) => {
    // Only show badge for revoked requests (deleted/revoked by user)
    if (status !== 'REJECTED' || rejectionReason !== 'Dicabut oleh user') {
      return null;
    }

    const statusConfig = {
      'REJECTED': { 
        class: 'badge-danger', 
        text: 'Revoked', 
        icon: '🚫'
      }
    };
    
    const config = statusConfig[status];
    if (!config) return null;
    
    return (
      <span className={`${styles.statusBadge} ${styles[config.class]}`}>
        <span className={styles.statusIcon}>{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      'website': '🌐',
      'android': '🤖',
      'ios': '📱',
      'backend': '⚙️'
    };
    return platformIcons[platform] || '💻';
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Loading component
  const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className={styles.requestApiContainer}>
      {/* Header Section */}
      <div className={styles.headerSection}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <div className={styles.breadcrumb}>
              <span className={styles.breadcrumbItem}>Dashboard</span>
              <span className={styles.breadcrumbSeparator}>•</span>
              <span className={styles.breadcrumbCurrent}>Request API Access</span>
            </div>
            <h1 className={styles.pageTitle}>
              <span className={styles.titleIcon}>🔑</span>
              Request API Access
            </h1>
            <p className={styles.pageDescription}>
              Ajukan permintaan akses API untuk mengintegrasikan aplikasi atau website Anda dengan layanan Piro
            </p>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="outline"
              size="large"
              onClick={() => setShowTechDocsModal(true)}
              icon="📚"
              className={styles.docsHeaderButton}
            >
              Tech Docs
            </Button>
            <Button
              variant="primary"
              size="large"
              onClick={() => setShowModal(true)}
              icon="✨"
              className={styles.primaryButton}
            >
              Buat API Access Baru
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsSection}>
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.statTotal}`}>
            <div className={styles.statIcon}>📊</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{requests.length}</div>
              <div className={styles.statLabel}>Total Requests</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statPending}`}>
            <div className={styles.statIcon}>⏳</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {requests.filter(r => r.status === 'PENDING').length}
              </div>
              <div className={styles.statLabel}>Pending</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statApproved}`}>
            <div className={styles.statIcon}>✅</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {requests.filter(r => r.status === 'APPROVED').length}
              </div>
              <div className={styles.statLabel}>Approved</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statRejected}`}>
            <div className={styles.statIcon}>❌</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {requests.filter(r => r.status === 'REJECTED' && r.rejectionReason !== 'Dicabut oleh user').length}
              </div>
              <div className={styles.statLabel}>Rejected</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statRevoked}`}>
            <div className={styles.statIcon}>🚫</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {requests.filter(r => r.status === 'REJECTED' && r.rejectionReason === 'Dicabut oleh user').length}
              </div>
              <div className={styles.statLabel}>Revoked</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <SimpleCard className={styles.requestsCard}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>📋</span>
              Daftar Request API
            </h2>
            <div className={styles.cardActions}>
              <Button
                variant="outline"
                size="medium"
                onClick={fetchRequests}
                icon={loading ? "🔄" : "🔄"}
                className={`${styles.refreshButton} ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>

          <div className={styles.requestsList}>
            {loading ? (
              <LoadingSpinner />
            ) : requests.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateContent}>
                  <div className={styles.emptyStateIcon}>🔐</div>
                  <h3 className={styles.emptyStateTitle}>Belum Ada Request API</h3>
                  <p className={styles.emptyStateDescription}>
                    Anda belum memiliki request API. Mulai integrasikan aplikasi Anda dengan API Piro.
                  </p>
                  <div className={styles.emptyStateFeatures}>
                    <div className={styles.featureItem}>
                      <span className={styles.featureIcon}>⚡</span>
                      <span>API Response Cepat</span>
                    </div>
                    <div className={styles.featureItem}>
                      <span className={styles.featureIcon}>🔒</span>
                      <span>Keamanan Terjamin</span>
                    </div>
                    <div className={styles.featureItem}>
                      <span className={styles.featureIcon}>📚</span>
                      <span>Dokumentasi Lengkap</span>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="large"
                    onClick={() => setShowModal(true)}
                    icon="�"
                    className={styles.ctaButton}
                  >
                    Request API Pertama
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.requestsGrid}>
                {requests.map((request, index) => (
                  <div key={index} className={styles.requestCard}>
                    <div className={styles.requestCardHeader}>
                      <div className={styles.requestInfo}>
                        <h3 className={styles.requestTitle}>{request.applicationName}</h3>
                        <p className={styles.requestUrl}>
                          <span className={styles.urlIcon}>🌐</span>
                          {request.websiteUrl}
                        </p>
                      </div>
                      <div className={styles.requestStatus}>
                        {getStatusBadge(request.status, request.rejectionReason)}
                      </div>
                    </div>

                    <div className={styles.requestCardBody}>
                      <div className={styles.requestDetail}>
                        <label className={styles.detailLabel}>
                          <span className={styles.labelIcon}>📱</span>
                          Platform:
                        </label>
                        <div className={styles.platformTags}>
                          {request.platform.map(platform => (
                            <span key={platform} className={styles.platformTag}>
                              {getPlatformIcon(platform)}
                              {platformOptions.find(p => p.value === platform)?.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className={styles.requestDetail}>
                        <label className={styles.detailLabel}>
                          <span className={styles.labelIcon}>🌍</span>
                          IP Address:
                        </label>
                        <div className={styles.ipAddresses}>
                          {request.serverIpAddress.split(',').map((ip, idx) => (
                            <code key={idx} className={styles.ipTag}>
                              {ip.trim()}
                            </code>
                          ))}
                        </div>
                      </div>

                      {(request.clientId || request.clientSecret) && (
                        <>
                          {request.clientId && (
                            <div className={styles.requestDetail}>
                              <label className={styles.detailLabel}>
                                <span className={styles.labelIcon}>🆔</span>
                                Client ID:
                              </label>
                              <div className={styles.apiKeyContainer}>
                                <code className={styles.apiKey}>
                                  {request.clientId}
                                </code>
                                <Button
                                  variant="outline"
                                  size="small"
                                  onClick={() => copyToClipboard(request.clientId, 'Client ID')}
                                  icon="📋"
                                  className={styles.copyButton}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          )}

                          {request.clientSecret && (
                            <div className={styles.requestDetail}>
                              <label className={styles.detailLabel}>
                                <span className={styles.labelIcon}>�</span>
                                Client Secret:
                              </label>
                              <div className={styles.apiKeyContainer}>
                                <code className={styles.apiKey}>
                                  {request.clientSecret}
                                </code>
                                <Button
                                  variant="outline"
                                  size="small"
                                  onClick={() => copyToClipboard(request.clientSecret, 'Client Secret')}
                                  icon="📋"
                                  className={styles.copyButton}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className={styles.requestCardFooter}>
                      <div className={styles.cardActions}>
                        {request.status === 'APPROVED' && (
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => setShowTechDocsModal(true)}
                            icon="📚"
                            className={styles.docsButton}
                          >
                            Tech Docs
                          </Button>
                        )}
                        
                        {(request.status === 'PENDING' || request.status === 'APPROVED') && 
                         request.rejectionReason !== 'Dicabut oleh user' && (
                          <Button
                            variant="danger"
                            size="small"
                            onClick={() => revokeApiRequest(request._id)}
                            loading={revokeLoading[request._id]}
                            icon="🗑️"
                            className={styles.revokeButton}
                          >
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SimpleCard>
      </div>

      {/* Request API Modal */}
      <Modal in={showModal} onClose={handleCloseModal}>
        <ModalWrapper>
          <ModalHead 
            title="✨ Buat API Access" 
            onClose={handleCloseModal} 
          />
          <ModalBody>
            <div className={styles.modalContent}>
              {/* Header Section */}
              <div className={styles.modalHeader}>
                <div className={styles.modalIcon}>🔑</div>
                <h3 className={styles.modalTitle}>Ajukan Akses API Piro</h3>
                <p className={styles.modalSubtitle}>
                  Integrasikan aplikasi Anda dengan mudah menggunakan API Piro yang powerful dan secure
                </p>
              </div>

              {/* Form Section */}
              <div className={styles.formSection}>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <span className={styles.labelIcon}>📱</span>
                      Nama Aplikasi/Website *
                    </label>
                    <input
                      type="text"
                      name="applicationName"
                      value={formData.applicationName}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="Contoh: Aplikasi Mobile Banking"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <span className={styles.labelIcon}>🌐</span>
                      URL Website *
                    </label>
                    <input
                      type="url"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <span className={styles.labelIcon}>⚙️</span>
                    Platform Aplikasi *
                  </label>
                  <div className={styles.platformGrid}>
                    {platformOptions.map(option => (
                      <div key={option.value} className={styles.platformCard}>
                        <input
                          type="checkbox"
                          id={`platform-${option.value}`}
                          checked={formData.platform.includes(option.value)}
                          onChange={() => handlePlatformChange(option.value)}
                          className={styles.platformCheckbox}
                        />
                        <label htmlFor={`platform-${option.value}`} className={styles.platformLabel}>
                          <div className={styles.platformIcon}>
                            {option.value === 'website' && '🌐'}
                            {option.value === 'android' && '🤖'}
                            {option.value === 'ios' && '📱'}
                            {option.value === 'backend' && '⚙️'}
                          </div>
                          <span className={styles.platformText}>{option.label}</span>
                          <div className={styles.checkmark}>✓</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <span className={styles.labelIcon}>🌍</span>
                    IP Address Server (Whitelist) *
                  </label>
                  <textarea
                    name="serverIpAddress"
                    value={formData.serverIpAddress}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    rows="3"
                    placeholder="192.168.1.1, 10.0.0.1 (pisahkan dengan koma jika lebih dari satu)"
                  />
                  <div className={styles.inputHint}>
                    💡 Masukkan IP address server yang akan mengakses API. Pisahkan dengan koma jika lebih dari satu.
                  </div>
                </div>

                {/* Info Card */}
                <div className={styles.infoCard}>
                  <div className={styles.infoHeader}>
                    <span className={styles.infoIcon}>ℹ️</span>
                    <strong>Informasi Penting</strong>
                  </div>
                  <ul className={styles.infoList}>
                    <li>✅ Request akan direview oleh tim teknis kami</li>
                    <li>⏱️ Proses approval membutuhkan waktu 1-3 hari kerja</li>
                    <li>📧 API key akan dikirim melalui email setelah disetujui</li>
                    <li>🔒 Pastikan semua data yang diisi sudah benar</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className={styles.modalActions}>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={handleCloseModal}
                    disabled={isLoading}
                    icon="❌"
                  >
                    Batal
                  </Button>
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleSubmit}
                    loading={isLoading}
                    icon="🚀"
                  >
                    Kirim Request
                  </Button>
                </div>
              </div>
            </div>
          </ModalBody>
        </ModalWrapper>
      </Modal>

      {/* Tech Docs Modal */}
      <Modal in={showTechDocsModal} onClose={() => setShowTechDocsModal(false)}>
        <ModalWrapper>
          <ModalHead 
            title="📚 Technical Documentation" 
            onClose={() => setShowTechDocsModal(false)} 
          />
          <ModalBody>
            <div className={styles.techDocsContent}>
              <div className={styles.docsHeader}>
                <div className={styles.docsIcon}>📖</div>
                <h3>Technical Documentation</h3>
                <p>Dokumentasi teknis lengkap untuk integrasi API Piro</p>
              </div>
              
              <div className={styles.docsSection}>
                <div className={styles.pdfPreview}>
                  <div className={styles.pdfIcon}>📄</div>
                  <h4>Piro Technical Documentation</h4>
                  <p>Dokumen PDF berisi panduan lengkap integrasi API, endpoint reference, dan contoh implementasi.</p>
                </div>
              </div>

              <div className={styles.docsActions}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleDownloadTechDocs}
                  icon="�"
                >
                  Download Tech Docs PDF
                </Button>
                <Button
                  variant="outline"
                  size="large"
                  onClick={() => setShowTechDocsModal(false)}
                  icon="❌"
                >
                  Tutup
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalWrapper>
      </Modal>
    </div>
  );
};

export default RequestAPI;
